// ============================================================
// TMR Portal Admin — cadastro/edição de Usuário
//
// Creating here invites the person into Clerk (06 §6.1), so this form is what actually gives someone
// access — not a row that waits for somebody to onboard them by hand. The e-mail is the correlation key
// across Clerk, V1 and V2 (05 §8): validated on create, immutable on edit, because changing it would
// detach the account from the identity both sides already match on.
//
// Cargo/time are gone with the mockup: a person's access comes from role grants (06 §5), which live on the
// authorization screens. There was never a "cargo" column behind them.
// ============================================================
import { useState } from 'react';
import type { MouseEvent } from 'react';
import { Btn } from '../../components/primitives';
import { TextField } from '../../components/fields';
import { PhoneField } from '../../components/PhoneField';
import { Icons, StrokeIcon } from '../../lib/icons';
import { isEmail } from '../../lib/format';
import { displayPhone, isValidInternationalPhone, toE164Phone } from '../../lib/phone';
import type { UserListItem } from '../../data/users/contract';
import type { AccessRoleGrant } from '../../data/authorization/contract';
import { useAuthorizationRoles } from '../../data/authorization/queries';
import { GrantsEditor, grantDraftError, toGrant } from '../authorization/GrantsEditor';
import type { GrantDraft } from '../authorization/GrantsEditor';
import { OverlayPortal } from '../../components/OverlayPortal';

/** What the form collects; the caller decides whether it becomes a create or an update. */
export interface UserFormValues {
  name: string;
  email: string;
  phone: string;
  /** Roles to grant on creation. Absent when editing — changing access is its own operation (06 §5.1). */
  grants?: AccessRoleGrant[];
}

export function UserFormModal({
  initial = null,
  saving = false,
  error = null,
  onClose,
  onSave,
}: {
  initial?: UserListItem | null;
  /** Disables the actions while the request is in flight, so a double click cannot submit twice. */
  saving?: boolean;
  /** Server-side failure to show inline — a duplicate e-mail, typically. */
  error?: string | null;
  onClose: () => void;
  onSave: (values: UserFormValues) => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  // The record holds E.164; the field opens on the readable spelling of it, as the other forms do.
  const [phone, setPhone] = useState(displayPhone(initial?.phone));
  const [grants, setGrants] = useState<GrantDraft[]>([]);
  const [touched, setTouched] = useState(false);

  // Only on creation: editing someone's identity and changing their access are different decisions with
  // different guards, and the roles panel is where the second one lives.
  const roles = useAuthorizationRoles();

  // Uniqueness is the server's to decide — it owns the constraint, and the client no longer holds the
  // whole list to check against. A duplicate comes back as a 409 and lands in `error`.
  const errs = {
    name: name.trim().length < 3 ? 'Informe o nome completo' : null,
    email: !isEmail(email) ? 'E-mail inválido' : null,
    phone: !isValidInternationalPhone(phone) ? 'Telefone inválido' : null,
  };
  const valid = !errs.name && !errs.email && !errs.phone;

  const submit = () => {
    if (!valid || (!initial && grants.some(grantDraftError))) {
      setTouched(true);
      return;
    }
    onSave({
      name: name.trim(),
      email: email.trim(),
      phone: toE164Phone(phone) ?? '',
      grants: initial ? undefined : grants.map(toGrant),
    });
  };

  const stop = (e: MouseEvent) => e.stopPropagation();

  return (
    <OverlayPortal>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(28,30,28,0.32)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div onClick={stop} style={{ width: initial ? 520 : 640, maxWidth: '100%', maxHeight: '90vh', background: 'var(--cream-50)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border-default)' }}>
            <div className="pa-eyebrow" style={{ marginBottom: 4 }}>{initial ? 'Editar usuário' : 'Novo usuário'}</div>
            <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 18, margin: 0, letterSpacing: '-0.01em' }}>
              {initial ? initial.name : 'Cadastrar e convidar'}
            </h3>
          </div>
          <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto' }}>
            <TextField label="Nome completo" required value={name} onChange={setName} error={touched && errs.name} placeholder="Ex.: Ana Beatriz Costa" />
            <TextField
              label="E-mail"
              required
              value={email}
              onChange={setEmail}
              error={touched && errs.email}
              placeholder="nome@tmrsaude.com.br"
              disabled={!!initial}
            />
            <PhoneField value={phone} onChange={setPhone} error={touched && errs.phone} />

            {!initial && (
              <>
                <div className="pa-eyebrow" style={{ marginTop: 4 }}>Papéis</div>
                <GrantsEditor
                  grants={grants}
                  onChange={setGrants}
                  roles={roles.data ?? []}
                  showErrors={touched}
                  emptyHint="Sem papel, a pessoa entra e não vê nada. Pode ser concedido depois."
                />
              </>
            )}

            {error && (
              <div role="alert" style={{ padding: '9px 12px', border: '1px solid var(--danger-400, var(--border-strong))', borderRadius: 'var(--radius-sm)', background: 'var(--cream-100)', fontSize: 12.5, color: 'var(--ink-900)' }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '9px 12px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', background: 'var(--cream-100)', fontSize: 12.5, color: 'var(--fg-muted)', lineHeight: 1.5 }}>
              <StrokeIcon d={Icons.shield} size={14} style={{ flexShrink: 0, marginTop: 2, color: 'var(--stone-400)' }} />
              {initial
                ? 'O e-mail identifica a conta e não pode ser alterado. Papéis são alterados pela coluna Papéis, na lista.'
                : 'A pessoa recebe um convite por e-mail para criar o acesso.'}
            </div>
          </div>
          <div style={{ padding: '14px 24px', borderTop: '1px solid var(--border-default)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <Btn variant="secondary" onClick={onClose} disabled={saving}>
              Cancelar
            </Btn>
            <Btn variant="primary" icon={Icons.check} onClick={submit} disabled={saving}>
              {saving ? 'Salvando…' : initial ? 'Salvar alterações' : 'Cadastrar e convidar'}
            </Btn>
          </div>
        </div>
      </div>
    </OverlayPortal>
  );
}
